/**
 * Where the API is — the one place that decides (W-10).
 *
 * **On the web it is relative**, `/api` on this same origin: no CORS, no second
 * hostname, and `connect-src 'self'` in `public/_headers` stays the whole
 * network surface. **In the native build** (`vite --mode native`) the bundle is
 * served from the WebView's own origin (`capacitor://localhost`,
 * `https://localhost`), where a relative `/api` would resolve to nothing, so it
 * is the absolute production origin — reached through CORS for exactly those
 * two origins (`server/cors.ts`).
 *
 * The mode is a build-time constant, so the web bundle folds this to `""` and
 * uses the same origin for public invite links. `scripts/check-invariants.sh` fails if
 * the absolute origin appears anywhere else in `src/`.
 */

/** The production origin the native apps call. Also the origin for native family invite links. */
export const PUBLIC_APP_ORIGIN = "https://tally.leeseidon.com";

/**
 * The origin to prefix API paths with, for a given Vite mode and debug origin.
 * Pure, for tests. The debug origin is a build-time constant that is `""` in
 * every release build, so the bundler folds this to the production origin and
 * a release bundle never contains a local one (the packaging guards in
 * `android/app/build.gradle` and `scripts/check-native-release-assets.sh`
 * scan every bundled asset for exactly that).
 */
export function apiOriginFor(mode: string, debugOrigin = ""): string {
  if (mode !== "native") return "";
  return debugOrigin === "" ? PUBLIC_APP_ORIGIN : debugOrigin;
}

export const API_ORIGIN = apiOriginFor(import.meta.env.MODE, __NATIVE_DEBUG_ORIGIN__);

/** Every API path starts here: `${API_BASE}/v1/...`. */
export const API_BASE = `${API_ORIGIN}/api`;

/** Web invites stay on the current deployment; native invites open the public app. */
export function inviteOrigin(): string {
  return import.meta.env.MODE === "native" ? PUBLIC_APP_ORIGIN : location.origin;
}
